import React, { useState, useRef, useEffect } from "react";
import PageLayout from "@/components/PageLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import Send from "@/components/custom/Send";
import { Helmet } from "react-helmet";
import Swal from "sweetalert2";

// Define the form schema with validation
const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  company: z.string().optional(),
  email: z
    .string()
    .email({ message: "Invalid email address." })
    .refine(
      (email) => {
        const validDomains = [
          "gmail.com",
          "outlook.com",
          "yahoo.fr",
          "ucac-icam.com",
          "icam.fr",
        ];
        const domain = email.split("@")[1];
        return validDomains.includes(domain);
      },
      {
        message:
          "Le domaine de votre e-mail n'est pas reconnu...",
      }
    ),
  message: z
    .string()
    .min(10, { message: "Trop court..." }),
});

// Configuration for development mode
const CONFIG = {
  isDevelopment:
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1",
  skipTurnstileInDev: true,
  googleScriptUrl: "https://script.google.com/macros/s/AKfycbzc2LtZgRvcAntvnqIJ2QcHCAjfYCOyDkjU69eV4FaE9ATp88H4Q9i4k9SRevmO8aSf/exec"
};

type FormValues = z.infer<typeof formSchema>;

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isTurnstileVerified, setIsTurnstileVerified] = useState(CONFIG.isDevelopment && CONFIG.skipTurnstileInDev);
  const turnstileRef = useRef<HTMLDivElement>(null);

  // Initialize form with react-hook-form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      company: "",
      email: "",
      message: "",
    },
  });

  // Initialize Turnstile when component mounts - only in production
  useEffect(() => {
    // Skip Turnstile initialization in development mode if configured
    if (CONFIG.isDevelopment && CONFIG.skipTurnstileInDev) {
      console.log("Development mode: Turnstile verification bypassed");
      return;
    }

    // Check if turnstile is already loaded
    if (window.turnstile && turnstileRef.current) {
      window.turnstile.render(turnstileRef.current, {
        sitekey: "0x4AAAAAAAgM-GMHUQmg6jYW",
        callback: (token: string) => {
          setTurnstileToken(token);
          setIsTurnstileVerified(true);
         
        },
      });
    }
  }, [toast]);

  const onSubmit = async (data: FormValues) => {
    // Skip Turnstile verification in development mode if configured
    if (!isTurnstileVerified && !(CONFIG.isDevelopment && CONFIG.skipTurnstileInDev)) {
      // toast({
      //   title: "Verification requise",
      //   description: "Verifier votre identité avant d'envoyer.",
      //   variant: "destructive",
      // });
      return;
    }

    setIsSubmitting(true);

    try {

      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast({
        title: "Message envoyé !",
        description: "Merci de patienter, je vous réponds dans pas longtemps.",
      });

      form.reset();
      setIsTurnstileVerified(false);
      setTurnstileToken(null);


      // Create FormData object to send to Google Apps Script
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('company', data.company || '');
      formData.append('email', data.email);
      formData.append('message', data.message);
      formData.append('turnstileToken', turnstileToken || '');
      formData.append('devMode', CONFIG.isDevelopment.toString());
      
      // Convert FormData to URLSearchParams for better compatibility
      const params = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        params.append(key, value.toString());
      }
      
      console.log("Submitting form in", CONFIG.isDevelopment ? "development" : "production", "mode");
      
      // Send data to Google Apps Script
      const response = await fetch(CONFIG.googleScriptUrl, {
        method: 'POST',
        mode: 'cors',
        body: params,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      
      // Process response
      const responseText = await response.text();
      let result;
      
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        console.log('Response is not JSON:', responseText.substring(0, 100));
        // If response is not JSON but status is OK, we'll assume success
        result = { success: response.ok, message: 'Message envoyé' };
      }
      
      if (result.success) {
      
        
      // Show success message
      Swal.fire({
        title: "Message envoyé !",
        text: "Je vous répondrai dès que possible !",
        icon: "success",
        confirmButtonText: "Fermer",
        confirmButtonColor: "#4163eb",
      });

        // Reset form and verification
        form.reset();
        
        // Only reset Turnstile if not in development mode
        if (!(CONFIG.isDevelopment && CONFIG.skipTurnstileInDev)) {
          setIsTurnstileVerified(false);
          setTurnstileToken(null);
          
          // Reset Turnstile
          if (window.turnstile && turnstileRef.current) {
            window.turnstile.reset(turnstileRef.current);
          }
        } else {
          // In development, keep the verification status true
          setIsTurnstileVerified(true);
        }
      } else {
        throw new Error(result.message || "Erreur lors de l'envoi du message");
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "OOOuups !",
        description: "Impossible d'envoyer ce message, Réessayer...",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Helmet>
    <title>Mopaossi | Contact</title>
    <meta
      name="description"
      content="Développeur Frontend | No-code| Low-code | UI/UX | Agilité"
    />
    <meta property="og:title" content="Mopaossi | Développeur Créatif" />
    <meta
      property="og:description"
      content="Découvrer un créateur, digitalisation, tech, app, dev : Mes sujets préférés."
    />
    <meta property="og:type" content="website" />
    <link rel="canonical" href="https://cbenji.pages.dev" />
  </Helmet>
    <PageLayout
      title="Mopaossi | Contact "
      description="Tu voudrais en savoir un peu plus..."
    >
      <div className="flex flex-col lg:flex-row items-center max-w-6xl mx-auto">
        {/* Image container - only visible on desktop (lg screens and up) */}
        <div className="hidden lg:block lg:w-2/5 pr-8">
          <div className="relative">
            <img 
              src="/bucket/contact.png" 
              alt="Contact illustration" 
              className="w-full h-auto"
            />
        </div>
        </div>
        
        {/* Form container */}
        <div className="w-full lg:w-3/5">
          <Card className="p-6 md:p-8 bg-gradient-to-b from-blue-700 via-blue-800 to-gray-900 text-white border-none shadow-xl">
            <div className="space-y-8">
              <div className="space-y-2">
                <h1 className="text-4xl md:text-5xl font-bold">
                  Salut,<span className="text-yellow-400"></span>
                </h1>

                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-6"
                  >
                    <div className="space-y-4">
                      <div className="flex flex-col">
                        <h2 className="text-3xl md:text-4xl font-bold mb-2">
                         Je m'appelle
                        </h2>
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  placeholder=""
                                  className="bg-transparent border-b border-t-0 border-x-0 rounded-none px-0 text-xl focus-visible:ring-0 focus-visible:border-white"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="flex flex-col">
                        <h2 className="text-3xl md:text-4xl font-bold mb-2">
                         Je suis
                        </h2>
                        <FormField
                          control={form.control}
                          name="company"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  placeholder=""
                                  className="bg-transparent border-b border-t-0 border-x-0 rounded-none px-0 text-xl focus-visible:ring-0 focus-visible:border-white"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="flex flex-col">
                        <h2 className="text-3xl md:text-4xl font-bold mb-2">
                          Mon E-mail est
                        </h2>
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  placeholder=""
                                  className="bg-transparent border-b border-t-0 border-x-0 rounded-none px-0 text-xl focus-visible:ring-0 focus-visible:border-white"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="flex flex-col">
                        <h2 className="text-3xl md:text-4xl font-bold mb-2">
                          J'aimerais parler de
                        </h2>
                        <FormField
                          control={form.control}
                          name="message"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Textarea
                                  placeholder="Le sujet que tu souhaites aborder......."
                                  className="bg-transparent border-b border-t-0 border-x-0 rounded-none px-0 text-xl focus-visible:ring-0 focus-visible:border-white min-h-[100px]"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex flex-col">
                       
                        {/* Only show Turnstile in production mode */}
                        {!(CONFIG.isDevelopment && CONFIG.skipTurnstileInDev) ? (
                          <div className="flex flex-col items-start">
                            <div
                              ref={turnstileRef}
                              id="turnstile-container"
                              className="mb-4"
                            ></div>
                            {!isTurnstileVerified && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  if (window.turnstile && turnstileRef.current) {
                                    window.turnstile.reset(turnstileRef.current);
                                  }
                                }}
                                className="hover:text-white hover:bg-gradient-to-br shadow-lg dark:shadow-lg font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2 flex items-center gap-2 bg-black/10 text-white"
                              >
                              </Button>
                            )}
                            {isTurnstileVerified && (
                              <div className="text-green-400 flex items-center gap-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-yellow-400 flex items-center gap-2 mb-4">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M12 16v.01M12 8v4" />
                              <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" />
                            </svg>
                          </div>
                        )}
                      </div>

                      <Send isSubmitting={isSubmitting} isTurnstileVerified={isTurnstileVerified}>
                      </Send>
                    </div>
                  </form>
                </Form>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
    </>
  );
};

export default Contact;

// Add TypeScript declaration for Turnstile
declare global {
  interface Window {
    turnstile: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          [key: string]: string | ((token: string) => void);
        }
      ) => string;
      reset: (container: HTMLElement | string) => void;
    };
  }
}